<template>
  <div>
    <label class="form-label mb-2">
      Classifica&ccedil;&atilde;o fiscal NFS-e*
    </label>
    <div class="position-relative">
      <div v-if="classification" class="d-flex align-items-center gap-2 mb-2">
        <span class="badge bg-primary text-wrap text-start">
          {{ classification.ctrib_nac }} — {{ classification.ctrib_nac_description }}
        </span>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          @click="clearClassification"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
      <input
        v-if="!classification"
        type="text"
        class="form-control"
        placeholder="Buscar por descrição, cTribNac, item da LC 116 ou NBS"
        v-model="classificationQuery"
        @input="searchClassifications(classificationQuery)"
        @focus="showDropdown = true"
        @blur="hideDropdownDelayed"
        autocomplete="off"
      />
      <div
        v-if="showDropdown && (classificationLoading || classificationResults.length)"
        class="position-absolute w-100 bg-white border rounded shadow-sm"
        style="z-index: 1000; max-height: 260px; overflow-y: auto;"
      >
        <div
          v-if="classificationLoading"
          class="text-center py-2 text-muted small"
        >
          Buscando...
        </div>
        <ul class="list-unstyled mb-0" v-else>
          <li
            v-for="item in classificationResults"
            :key="item.id"
            class="px-3 py-2 border-bottom classification-item"
            style="cursor: pointer;"
            @mousedown.prevent="selectClassification(item)"
          >
            <div class="small fw-bold">{{ item.ctrib_nac_description }}</div>
            <div class="small text-muted" v-if="item.lc116_description">
              <span v-if="item.lc116_item">LC 116 {{ item.lc116_item }}: </span>
              {{ item.lc116_description }}
            </div>
            <div class="small text-secondary">
              cTribNac: {{ item.ctrib_nac }} &middot;
              {{ optionsCountLabel(item) }}
            </div>
          </li>
        </ul>
      </div>
      <div
        v-if="showDropdown && !classificationResults.length && classificationQuery.length >= 2 && !classificationLoading"
        class="position-absolute w-100 bg-white border rounded shadow-sm px-3 py-2 text-muted small"
        style="z-index: 1000;"
      >
        Nenhuma classificação encontrada.
      </div>
    </div>

    <div v-if="classification" class="row mt-3">
      <div class="col-lg-4 col-md-6 col-sm-12 mb-3">
        <label for="input-nbs" class="form-label mb-2">
          C&oacute;digo NBS*
        </label>
        <select
          id="input-nbs"
          class="form-select"
          :value="nbs ? nbs.id : ''"
          @change="selectOption('nbs', $event.target.value)"
        >
          <option value="">Selecione...</option>
          <option v-for="option in nbsOptions" :key="option.id" :value="option.id">
            {{ option.nbs_formatted_code || option.nbs_code || `Código #${option.id}` }}
            <template v-if="option.nbs_description"> — {{ option.nbs_description }}</template>
          </option>
        </select>
        <div class="form-text" v-if="nbsUnavailable">
          Esta classificação não possui código NBS correlacionado.
        </div>
      </div>
    </div>

    <div v-if="classification" class="mt-3">
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        @click="showIbsCbs = !showIbsCbs"
      >
        <i class="fas" :class="showIbsCbs ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        Dados IBS/CBS (cIndOp, cClassTrib) — opcional até 2027
      </button>
    </div>

    <div v-if="classification && showIbsCbs" class="row mt-3">
      <div class="col-lg-4 col-md-6 col-sm-12 mb-3">
        <label for="input-ind-op" class="form-label mb-2">
          Indicador de opera&ccedil;&atilde;o (cIndOp)
        </label>
        <select
          id="input-ind-op"
          class="form-select"
          :value="indOp ? indOp.id : ''"
          @change="selectOption('indOp', $event.target.value)"
        >
          <option value="">Não informar</option>
          <option v-for="option in indOpOptions" :key="option.id" :value="option.id">
            {{ option.code || `Código #${option.id}` }}
            <template v-if="option.local_incidencia"> — {{ option.local_incidencia }}</template>
          </option>
        </select>
        <div class="form-text">Define o local de incidência do IBS/CBS.</div>
      </div>

      <div class="col-lg-4 col-md-6 col-sm-12 mb-3">
        <label for="input-class-trib" class="form-label mb-2">
          Classifica&ccedil;&atilde;o tribut&aacute;ria IBS/CBS (cClassTrib)
        </label>
        <select
          id="input-class-trib"
          class="form-select"
          :value="classTrib ? classTrib.id : ''"
          @change="selectOption('classTrib', $event.target.value)"
        >
          <option value="">Não informar</option>
          <option v-for="option in classTribOptions" :key="option.id" :value="option.id">
            <template v-if="option.code">{{ option.code }} (CST {{ option.cst }}) — {{ option.description }}</template>
            <template v-else>Código #{{ option.id }}</template>
          </option>
        </select>
        <div class="form-text" v-if="classTribUnavailable">
          Esta classificação não possui classificação tributária (cClassTrib) correlacionada.
        </div>
      </div>
    </div>

    <div v-if="classification" class="card bg-light p-3 mt-3">
      <small class="text-muted fw-bold mb-2">Prévia fiscal para emissão</small>
      <div class="small">
        <strong>C&oacute;digo de Tributa&ccedil;&atilde;o Nacional</strong>:
        {{ classification.ctrib_nac }}
        <span v-if="classification.ctrib_nac_description"> — {{ classification.ctrib_nac_description }}</span>
      </div>
      <div class="small mt-1" v-if="classification.lc116_item">
        <strong>Item da LC 116</strong>:
        {{ classification.lc116_item }}
        <span v-if="classification.lc116_description"> — {{ classification.lc116_description }}</span>
      </div>
      <div class="small mt-1">
        <strong>NBS</strong>:
        <span v-if="nbs">
          {{ nbs.nbs_formatted_code || nbs.nbs_code || `Código #${nbs.id}` }}
          <span v-if="nbs.nbs_description"> — {{ nbs.nbs_description }}</span>
        </span>
        <span v-else class="text-muted">não informado</span>
      </div>
      <div class="small mt-1">
        <strong>Indicador de opera&ccedil;&atilde;o</strong>:
        <span v-if="indOp">
          {{ indOp.code || `Código #${indOp.id}` }}
          <span v-if="indOp.local_incidencia"> — {{ indOp.local_incidencia }}</span>
        </span>
        <span v-else class="text-muted">não informado</span>
      </div>
      <div class="small mt-1">
        <strong>Classifica&ccedil;&atilde;o tribut&aacute;ria</strong>:
        <span v-if="classTrib">
          <template v-if="classTrib.code">{{ classTrib.code }} (CST {{ classTrib.cst }}) — {{ classTrib.description }}</template>
          <template v-else>Código #{{ classTrib.id }}</template>
        </span>
        <span v-else class="text-muted">não informado</span>
      </div>
    </div>
  </div>
</template>
<script>
import api from "@/services/api";

export default {
  name: "nfse-classification-picker",
  props: {
    classification: {
      type: Object,
      default: null,
    },
    nbs: {
      type: Object,
      default: null,
    },
    indOp: {
      type: Object,
      default: null,
    },
    classTrib: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:classification", "update:nbs", "update:indOp", "update:classTrib"],
  data() {
    return {
      classificationQuery: "",
      classificationResults: [],
      classificationLoading: false,
      classificationDebounce: null,
      showDropdown: false,
      showIbsCbs: false,
    };
  },
  watch: {
    // Abre a seção automaticamente se algum dado de IBS/CBS já estiver
    // preenchido (ex.: hidratado na edição de um serviço já cadastrado).
    indOp(value) {
      if (value) this.showIbsCbs = true;
    },
    classTrib(value) {
      if (value) this.showIbsCbs = true;
    },
  },
  computed: {
    nbsOptions() {
      return this.optionsFor("nbsOptions", this.nbs);
    },
    indOpOptions() {
      return this.optionsFor("indOpOptions", this.indOp);
    },
    classTribOptions() {
      return this.optionsFor("classTribOptions", this.classTrib);
    },
    nbsUnavailable() {
      return this.isReallyEmpty("nbsOptions") && !this.nbs;
    },
    classTribUnavailable() {
      return this.isReallyEmpty("classTribOptions") && !this.classTrib;
    },
  },
  beforeUnmount() {
    clearTimeout(this.classificationDebounce);
  },
  methods: {
    // Une a lista de opções ativas com a opção já selecionada, caso ela esteja
    // inativa ou o array não tenha vindo no preload — evita que a seleção
    // hidratada (ex.: no formulário de edição) suma da tela.
    optionsFor(key, selected) {
      const list = (this.classification && this.classification[key]) || [];
      const active = list.filter((o) => o.is_active !== false);
      if (selected && !active.some((o) => String(o.id) === String(selected.id))) {
        return [selected, ...active];
      }
      return active;
    },
    isReallyEmpty(key) {
      if (!this.classification) return false;
      const list = this.classification[key];
      return !Array.isArray(list) || list.length === 0;
    },
    pickSole(list) {
      const active = (list || []).filter((o) => o.is_active !== false);
      return active.length === 1 ? active[0] : null;
    },
    optionsCountLabel(item) {
      const nbsTotal = (item.nbsOptions || []).length;
      const parts = [nbsTotal ? (nbsTotal === 1 ? "1 NBS" : `${nbsTotal} NBS`) : "sem NBS"];
      const indTotal = (item.indOpOptions || []).length;
      const tribTotal = (item.classTribOptions || []).length;
      if (indTotal > 1) parts.push(`${indTotal} indicadores`);
      if (tribTotal > 1) parts.push(`${tribTotal} class. tributárias`);
      else if (!tribTotal) parts.push("sem cClassTrib");
      return parts.join(" · ");
    },
    searchClassifications(q) {
      if (!q || q.length < 2) {
        this.classificationResults = [];
        return;
      }
      clearTimeout(this.classificationDebounce);
      this.classificationDebounce = setTimeout(async () => {
        this.classificationLoading = true;
        try {
          const { data } = await api.get("/company/nfse/service-classifications", {
            params: { q, limit: 20 },
          });
          this.classificationResults = data.data || data || [];
        } catch {
          this.classificationResults = [];
        } finally {
          this.classificationLoading = false;
        }
      }, 300);
    },
    selectClassification(item) {
      this.$emit("update:classification", item);
      this.$emit("update:nbs", this.pickSole(item.nbsOptions));
      this.$emit("update:indOp", this.pickSole(item.indOpOptions));
      this.$emit("update:classTrib", this.pickSole(item.classTribOptions));
      this.classificationQuery = "";
      this.classificationResults = [];
      this.showDropdown = false;
    },
    selectOption(kind, value) {
      if (!value) {
        this.$emit(`update:${kind}`, null);
        return;
      }
      const list = this[`${kind}Options`];
      const selected = list.find((option) => String(option.id) === String(value));
      this.$emit(`update:${kind}`, selected || null);
    },
    clearClassification() {
      this.$emit("update:classification", null);
      this.$emit("update:nbs", null);
      this.$emit("update:indOp", null);
      this.$emit("update:classTrib", null);
      this.classificationQuery = "";
      this.classificationResults = [];
    },
    hideDropdownDelayed() {
      setTimeout(() => {
        this.showDropdown = false;
      }, 200);
    },
  },
};
</script>

<style scoped>
.classification-item:hover {
  background-color: #f8f9fa;
}
</style>
